import type { DeploymentMetricsSpectrumRow } from 'common/deployment/MetricsDocument';

const emptySpectrumPoint = { gray: 240, green: 0, red: 0, yellow: 0 };

const emptyNumericPoint = { value: 0 };

export const expandDataToFillSlices = <T extends { time: string | Date }>(
  data: T[],
  slices: number,
  activePeriod: '1m' | '1h' | '1d' | null,
  type: 'spectrum' | 'numeric'
): T[] => {
  if (!activePeriod) return [];

  const emptyPoint = (type === 'spectrum' ? emptySpectrumPoint : emptyNumericPoint) as unknown as Omit<T, 'time'>;

  const interval = activePeriod == '1m' ? 1 : activePeriod == '1h' ? 60 : 24 * 60;
  const points: T[] = [];
  let index = data.length;
  const lastDate = new Date();
  lastDate.setSeconds(0);
  let lastTime = lastDate.getTime();
  do {
    index--;

    if (data.length === 0 && points.length === 0) {
      points.unshift({ ...emptyPoint, time: new Date(lastTime).toISOString() } as T);
      continue;
    }

    if (points.length === 0) {
      points.unshift(data[index]);
      lastTime = new Date(data[index].time).getTime();
      continue;
    }

    const point = data[index];
    const timeToPrevious = interval * 60000;

    if (!point) {
      points.unshift({ ...emptyPoint, time: new Date(lastTime - timeToPrevious).toISOString() } as T);
      lastTime = lastTime - timeToPrevious;
      continue;
    }

    if (lastTime - new Date(point.time).getTime() > timeToPrevious) {
      points.unshift({ ...emptyPoint, time: new Date(lastTime - timeToPrevious).toISOString() } as T);
      lastTime = lastTime - timeToPrevious;
      index++;
      continue;
    }

    points.unshift(point);
    lastTime = new Date(point.time).getTime();
  } while (points.length < slices);

  return points;
};

export const dataRowToProps = (row: DeploymentMetricsSpectrumRow) => {
  const dataPointCount = row.gray + row.red + row.yellow + row.green;

  const colorIndexes = ['gray', 'red', 'yellow', 'green'];
  const colorNames = ['Unknown', 'Error', 'Warning', 'Success'];
  const colorCounts = [row.gray, row.red, row.yellow, row.green];

  // Analyze statuses
  // NOTE! As main color we should the most frequent status, as secondary color we should the worst case status used even once
  //       One issue is that if most frequent is red, we will never show that there has been some other statuses, good or bad ??
  const { mostFrequentColorIndex, worstColorIndex, percentageDescriptions } = colorCounts.reduce(
    (prev, count, index) => {
      if (count > prev.maxCount) {
        prev.mostFrequentColorIndex = index;
        prev.maxCount = count;
      }
      if (count > 0 && prev.worstColorIndex == -1) {
        prev.worstColorIndex = index;
      }
      if (count > 0 && count != dataPointCount) {
        prev.percentageDescriptions.push(`${colorNames[index]} ${Math.round((count / dataPointCount) * 100)}%`);
      }
      return prev;
    },
    { mostFrequentColorIndex: 0, maxCount: 0, worstColorIndex: -1, percentageDescriptions: [] as string[] }
  );

  const title =
    new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(row.time)) +
    ' ' +
    percentageDescriptions.join(' ');

  return {
    key: `${new Date(row.time).getTime()}`,
    title,
    major: colorIndexes[mostFrequentColorIndex],
    minor: colorIndexes[worstColorIndex],
  };
};
